import React from 'react';
import { Box, Typography } from '@mui/material';

const A4_W = 595;
const A4_H = 842;
const C = 'var(--color-primary)';
const CL = '#e6faf7';

const fmtDate = (d?: string) =>
    d ? new Date(d).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' }) : '1 January 2025';

const fmtAmt = (amt: number, cur = 'AUD') => `${cur.toUpperCase()} ${amt.toFixed(2)}`;

export interface InvoicePreviewMockProps {
    // Branding / layout (from invoice settings)
    statusName: string;
    logoUrl: string | null;
    companyName: string;
    fromAddress?: string;
    fromPhone?: string;
    fromFax?: string;
    fromEmail?: string;
    titlePosition?: 'left' | 'right';
    hideHeaderImage?: boolean;
    showTitle?: boolean;
    showContactInfo?: boolean;
    // Live invoice data (optional — falls back to placeholder values)
    termsAndConditions?: string;
    invoiceDate?: string;
    invoiceNumber?: string;
    recipientName?: string;
    clientCompanyName?: string;
    clientAddress?: string;
    clientAddress2?: string;
    clientSuburb?: string;
    clientState?: string;
    clientPostalCode?: string;
    clientPhone?: string;
    clientEmail?: string;
    items?: Array<{ type?: 'item' | 'comment'; description: string; quantity: number; unitPrice: number; lineTotal: number; taxLabels?: string[]; commentHtml?: string }>;
    taxes?: Array<{ name: string; amount: number }>;
    subtotal?: number;
    total?: number;
    currency?: string;
}

export const InvoicePreviewMock: React.FC<InvoicePreviewMockProps> = ({
    statusName, logoUrl, companyName, fromAddress, fromPhone, fromFax, fromEmail,
    titlePosition = 'left', hideHeaderImage = false,
    showTitle = true, showContactInfo = true,
    termsAndConditions = '',
    invoiceDate, invoiceNumber,
    recipientName, clientCompanyName, clientAddress, clientAddress2,
    clientSuburb, clientState, clientPostalCode, clientPhone, clientEmail,
    items, taxes, subtotal, total, currency = 'AUD',
}) => {
    const title = statusName.toUpperCase();
    const otherSide: 'left' | 'right' = titlePosition === 'right' ? 'left' : 'right';
    const BANNER_H = 110;

    const hasLiveData = !!(recipientName || clientCompanyName || (items && items.length > 0));

    // Bill-to address lines
    const clientLines = hasLiveData ? [
        clientCompanyName || null,
        clientAddress || null,
        clientAddress2 || null,
        [clientSuburb, clientState].filter(Boolean).join(', ') + (clientPostalCode ? ` ${clientPostalCode}` : '') || null,
        clientPhone ? `Ph: ${clientPhone}` : null,
        clientEmail || null,
    ].filter(Boolean) as string[] : ['Acme Corporation', '123 Example Street', 'Melbourne VIC 3000', 'john@acme.com'];

    // Items
    const displayItems = (hasLiveData && items && items.length > 0) ? items : [
        { description: 'Professional services — consulting', quantity: 10, unitPrice: 150, lineTotal: 1500 },
        { description: 'Project management', quantity: 5, unitPrice: 200, lineTotal: 1000 },
        { description: 'Software licence fee', quantity: 1, unitPrice: 499, lineTotal: 499 },
    ] as typeof items;

    // Totals
    const displaySubtotal = subtotal ?? 2999;
    const displayTotal = total ?? 3298.9;
    const displayTaxes: Array<{ name: string; amount: number }> = (taxes && taxes.length > 0) ? taxes : [{ name: 'GST (10%)', amount: 299.9 }];

    const displayInvoiceNumber = invoiceNumber || 'INV-0000001';
    const displayDate = fmtDate(invoiceDate);
    const displayTotal$ = fmtAmt(displayTotal, currency);

    const tableRow = (desc: string, qty: string, price: string, amt: string, bg = '#fff', taxLabels?: string[]) => (
        <Box sx={{ display: 'flex', borderBottom: '1px solid #e2e8f0', background: bg }}>
            <Box sx={{ flex: 3, px: 1.5, py: 1, fontSize: 11, color: 'var(--color-text-muted)' }}>{desc}</Box>
            <Box sx={{ flex: 1, px: 1.5, py: 1, fontSize: 11, color: 'var(--color-text-muted)', textAlign: 'right' }}>{qty}</Box>
            <Box sx={{ flex: 1.5, px: 1.5, py: 1, fontSize: 9, color: C }}>{taxLabels?.join(', ') || ''}</Box>
            <Box sx={{ flex: 1, px: 1.5, py: 1, fontSize: 11, color: 'var(--color-text-muted)', textAlign: 'right' }}>{price}</Box>
            <Box sx={{ flex: 1, px: 1.5, py: 1, fontSize: 11, color: 'var(--color-text-muted)', textAlign: 'right' }}>{amt}</Box>
        </Box>
    );

    return (
        <Box sx={{ width: 595, minHeight: 842, background: 'var(--color-page-background)', fontFamily: 'Helvetica, Arial, sans-serif', fontSize: 11 }}>
            {/* Header banner */}
            {!hideHeaderImage && (
            <Box sx={{ position: 'relative', height: BANNER_H, background: '#f7f8fa', overflow: 'hidden' }}>
                <Box sx={{ height: BANNER_H, display: 'flex', justifyContent: 'center' }}>
                    {logoUrl
                        ? <img src={logoUrl} alt="header" style={{ width: '100%', height: BANNER_H, objectFit: 'cover' }} />
                        : <Box sx={{ width: '100%', height: BANNER_H, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography sx={{ fontSize: 9, color: '#a0aec0' }}>Header image</Typography>
                          </Box>
                    }
                </Box>
                {showTitle && (
                    <Box sx={{ position: 'absolute', top: 14, [titlePosition]: 16, background: 'rgba(255,255,255,0.7)', borderRadius: 0.5, px: 1.75, py: 1 }}>
                        <Typography sx={{ fontSize: 28, fontWeight: 800, color: C, letterSpacing: 1 }}>{title}</Typography>
                    </Box>
                )}
            </Box>
            )}

            {/* Contact info strip — below banner, aligned opposite the title */}
            {showContactInfo && (
            <Box sx={{ px: 4.5, pt: 1.5, pb: 0.5, display: 'flex', justifyContent: otherSide === 'right' ? 'flex-end' : 'flex-start' }}>
                <Box>
                    {[
                        companyName || 'Your Company Name',
                        'ABN: 12 345 678 901',
                        fromAddress || null,
                        fromPhone ? `Ph: ${fromPhone}` : null,
                        fromFax ? `Fax: ${fromFax}` : null,
                        fromEmail || null,
                    ].filter(Boolean).map((l, i) => (
                        <Typography key={i} sx={{ fontSize: i === 0 ? 9 : 8, fontWeight: i === 0 ? 700 : 400, color: i === 0 ? C : 'var(--color-text-muted)', lineHeight: 1.5 }}>
                            {l}
                        </Typography>
                    ))}
                </Box>
            </Box>
            )}

            <Box sx={{ px: 4.5, pt: hideHeaderImage ? 5 : 2, pb: 6 }}>
                {/* Meta row */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: titlePosition === 'right' ? 'flex-end' : 'flex-start', gap: 0.5, mb: 2 }}>
                    {[['Invoice Date', displayDate], ['Invoice Number', displayInvoiceNumber]].map(([l, v]) => (
                        <Box key={l} sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                            <Typography sx={{ fontSize: 8, color: 'var(--color-text-muted)' }}>{l}</Typography>
                            <Typography sx={{ fontSize: 8, fontWeight: 700, color: 'var(--color-default-text)' }}>{v}</Typography>
                        </Box>
                    ))}
                </Box>

                {/* Divider */}
                <Box sx={{ borderBottom: `2px solid ${C}`, mb: 2 }} />

                {/* Bill To */}
                <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: 7, fontWeight: 700, color: C, textTransform: 'uppercase', mb: 0.5 }}>To</Typography>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--color-default-text)', mb: 0.25 }}>
                        {recipientName || 'John Smith'}
                    </Typography>
                    {clientLines.map((l, i) => (
                        <Typography key={i} sx={{ fontSize: 8, color: 'var(--color-text-muted)', lineHeight: 1.6 }}>{l}</Typography>
                    ))}
                </Box>

                {/* Items table */}
                <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', background: C, borderRadius: '2px 2px 0 0' }}>
                        {[
                            { label: 'Description', flex: 3, right: false },
                            { label: 'Qty', flex: 1, right: true },
                            { label: 'Tax', flex: 1.5, right: false },
                            { label: 'Unit Price', flex: 1, right: true },
                            { label: 'Amount', flex: 1, right: true },
                        ].map(({ label, flex, right }) => (
                            <Box key={label} sx={{ flex, px: 1.5, py: 0.75, fontSize: 8, fontWeight: 700, color: '#fff', textTransform: 'uppercase', textAlign: right ? 'right' : 'left' }}>
                                {label}
                            </Box>
                        ))}
                    </Box>
                    {displayItems!.map((item, idx) => {
                        if (item.type === 'comment') {
                            return (
                                <Box key={idx} sx={{ borderBottom: '1px solid #e2e8f0', background: idx % 2 === 1 ? '#f7f8fa' : '#fff', px: 1.5, py: 0.75 }}>
                                    <Box sx={{
                                        fontSize: 10, color: 'var(--color-text-muted)', lineHeight: 1.5,
                                        '& p': { my: '2px' },
                                        '& ul, & ol': { my: '2px', pl: '1.4em', listStylePosition: 'outside' },
                                        '& ul': { listStyleType: 'disc' },
                                        '& ol': { listStyleType: 'decimal' },
                                        '& li': { display: 'list-item', my: '1px' },
                                        '& strong': { fontWeight: 700 },
                                        '& em': { fontStyle: 'italic' },
                                        '& u': { textDecoration: 'underline' },
                                    }}
                                    dangerouslySetInnerHTML={{ __html: item.commentHtml || '' }} />
                                </Box>
                            );
                        }
                        return tableRow(
                            item.description,
                            String(item.quantity),
                            fmtAmt(item.unitPrice, currency),
                            fmtAmt(item.lineTotal, currency),
                            idx % 2 === 1 ? '#f7f8fa' : '#fff',
                            item.taxLabels,
                        );
                    })}
                </Box>

                {/* Totals */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Box sx={{ width: 220 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #e2e8f0' }}>
                            <Typography sx={{ fontSize: 8, color: 'var(--color-text-muted)' }}>Subtotal</Typography>
                            <Typography sx={{ fontSize: 8, color: 'var(--color-default-text)' }}>{fmtAmt(displaySubtotal, currency)}</Typography>
                        </Box>
                        {displayTaxes.map((tax) => (
                            <Box key={tax.name} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #e2e8f0' }}>
                                <Typography sx={{ fontSize: 8, color: 'var(--color-text-muted)' }}>{tax.name}</Typography>
                                <Typography sx={{ fontSize: 8, color: 'var(--color-default-text)' }}>{fmtAmt(tax.amount, currency)}</Typography>
                            </Box>
                        ))}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', background: C, borderRadius: 0.5, px: 1.5, py: 0.875, mt: 0.5 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#fff' }}>TOTAL</Typography>
                            <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#fff' }}>{displayTotal$}</Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Terms */}
                {termsAndConditions && (
                    <Box sx={{ mb: 3 }}>
                        <Typography sx={{ fontSize: 7, fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', mb: 0.5 }}>Terms & Conditions</Typography>
                        <Typography sx={{ fontSize: 8, color: 'var(--color-text-muted)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                            {termsAndConditions.slice(0, 300)}{termsAndConditions.length > 300 ? '…' : ''}
                        </Typography>
                    </Box>
                )}

                {/* Payment advice */}
                <Box sx={{ borderTop: `1.5px dashed ${C}`, pt: 2 }}>
                    <Typography sx={{ fontSize: 9, fontWeight: 700, color: C, textAlign: 'center', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
                        Payment Advice (invoice and statement)
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', background: CL, borderLeft: `3px solid ${C}`, borderRadius: 0.5, px: 2, py: 1.25 }}>
                        {[['Invoice Number', displayInvoiceNumber], ['Invoice Date', displayDate], ['Amount Due', displayTotal$]].map(([l, v]) => (
                            <Box key={l}>
                                <Typography sx={{ fontSize: 7, color: 'var(--color-text-muted)', mb: 0.25 }}>{l}</Typography>
                                <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'var(--color-default-text)' }}>{v}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export const PreviewScaler: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [scale, setScale] = React.useState(0.5);

    React.useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const update = () => setScale(el.clientWidth / A4_W);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    return (
        <Box ref={containerRef} sx={{ border: '1px solid #e8e8e8', borderRadius: 2, overflow: 'hidden', background: 'var(--color-container-background)' }}>
            <Box sx={{ position: 'relative', height: Math.round(A4_H * scale), overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', top: 0, left: 0, width: A4_W, transformOrigin: 'top left', transform: `scale(${scale})` }}>
                    {children}
                </Box>
            </Box>
        </Box>
    );
};
